import JSZip from 'jszip';
import { apiHeaderOptions } from 'api/apiHeaderOptions';
import { UploadedFile } from '@komodo-design-system/komodo-core-web-components';
import api from 'api';
import { serviceUrl } from 'api/config';
import { BUCKET_NAME } from 'constants/constants';

export interface UploadProgress {
  show: boolean;
  completed: number;
  total: number;
  currentFile: string;
  stage: string;
}

export interface FileUploadResult {
  success: boolean;
  uploadedCount: number;
  errorCount: number;
  totalFiles: number;
  message: string;
}

export interface FileToUpload {
  fileData: Blob | File;
  fileName: string;
  displayName?: string;
}

export class FileUploadUtils {
  static async uploadFileToS3(fileData: Blob | File, fileName: string): Promise<boolean> {
    try {
      // Determine the correct content type
      let contentType = 'application/octet-stream';
      if (fileData instanceof File) {
        contentType = fileData.type || 'application/octet-stream';
        // Override for Excel files if browser doesn't detect correctly
        if (fileName.toLowerCase().endsWith('.xlsx') && contentType === 'application/octet-stream') {
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        } else if (fileName.toLowerCase().endsWith('.xls') && contentType === 'application/octet-stream') {
          contentType = 'application/vnd.ms-excel';
        }
      } else if (fileName.toLowerCase().endsWith('.pdf')) {
        contentType = 'application/pdf';
      } else if (fileName.toLowerCase().endsWith('.xlsx')) {
        contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (fileName.toLowerCase().endsWith('.xls')) {
        contentType = 'application/vnd.ms-excel';
      } else if (fileName.toLowerCase().match(/\.(jpg|jpeg)$/)) {
        contentType = 'image/jpeg';
      } else if (fileName.toLowerCase().endsWith('.png')) {
        contentType = 'image/png';
      }
      
      let bucketName = BUCKET_NAME.prod;
      if (serviceUrl.includes('qa4')) {
        bucketName = BUCKET_NAME.qa4;
      } else if (serviceUrl.includes('qa1')) {
        bucketName = BUCKET_NAME.qa1;
      }

      // Get signed URL
      const signedUrlApi = `${api.config.getSignedUrl('bulkupload')}/${fileName}&bucketName=${bucketName}&action=upload`;
      const apiHeader = apiHeaderOptions();
      const response = await fetch(signedUrlApi, apiHeader);
      
      if (!response.ok) {
        console.error('Error fetching signed URL:', response.status, fileName);
        return false;
      }

      const data = await response.json();
      const presignedUrl = data.success.preSignedURL;

      // Upload to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
        },
        body: fileData,
      });

      if (uploadResponse.ok) {
        return true;
      }
      
      console.error('Upload failed:', fileName, uploadResponse.status);
      return false;
    } catch (error) {
      console.error('Upload error for', fileName, ':', error);
      return false;
    }
  }

  static async calculateTotalFiles(filesList: UploadedFile[]): Promise<number> {
    const zipFileObj = filesList.find((file: any) => file.name.toLowerCase().endsWith('.zip'));
    const excelFiles = filesList.filter((file: any) => {
      const fileName = file.name.toLowerCase();
      return (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) && !fileName.endsWith('.zip');
    });
    const otherFiles = filesList.filter((file: any) => {
      const fileName = file.name.toLowerCase();
      return !fileName.endsWith('.zip') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls');
    });

    let totalFiles = excelFiles.length + otherFiles.length;
    
    if (zipFileObj?.fileReference) {
      const zip = await JSZip.loadAsync(zipFileObj.fileReference);
      const zipFileCount = Object.keys(zip.files).filter(key => !key.endsWith('/')).length;
      totalFiles += zipFileCount;
    }

    return totalFiles;
  }

  static categorizeFiles(filesList: UploadedFile[]) {
    const zipFileObj = filesList.find((file: any) => file.name.toLowerCase().endsWith('.zip'));
    const excelFiles = filesList.filter((file: any) => {
      const fileName = file.name.toLowerCase();
      return (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) && !fileName.endsWith('.zip');
    });
    const otherFiles = filesList.filter((file: any) => {
      const fileName = file.name.toLowerCase();
      return !fileName.endsWith('.zip') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls');
    });

    return { zipFileObj, excelFiles, otherFiles };
  }

  static async processZipFile(zipFileObj: any): Promise<FileToUpload[]> {
    const filesToUpload: FileToUpload[] = [];
    
    if (!zipFileObj?.fileReference) {
      return filesToUpload;
    }
    const zip = await JSZip.loadAsync(zipFileObj.fileReference);
    const fileEntries = Object.entries(zip.files).filter(
      ([, file]) => !file.dir
    );
    for (const [fileName, fileObj] of fileEntries) {
      try {
        const fileData = await fileObj.async('blob');
        filesToUpload.push({
          fileData,
          fileName,
          displayName: `${fileName} (from ${zipFileObj.name})`
        });
      } catch (error) {
        console.error(`Error processing file ${fileName}:`, error);
      }
    }

    return filesToUpload;
  }

  static processExcelFiles(excelFiles: any[], jobId: string): FileToUpload[] {
    const filesToUpload: FileToUpload[] = [];
    
    for (const file of excelFiles) {
      if (!file.fileReference) {
        continue;
      }
      
      // Generate new filename with job ID
      const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
      const newFileName = `${jobId}${fileExtension}`;
     
      filesToUpload.push({
        fileData: file.fileReference,
        fileName: newFileName,
        displayName: `${file.name} (uploaded as ${newFileName})`
      });
    }

    return filesToUpload;
  }

  static processOtherFiles(otherFiles: any[]): FileToUpload[] {
    const filesToUpload: FileToUpload[] = [];
    
    for (const file of otherFiles) {
      if (!file.fileReference) {
        continue;
      }
      
      // Convert File to Blob to match JSZip extracted files format
      const fileBlob = new Blob([file.fileReference], { type: file.fileReference.type });
      filesToUpload.push({
        fileData: fileBlob,
        fileName: file.name,
        displayName: file.name
      });
    }

    return filesToUpload;
  }
}
